import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { supabase, SecretNotification, subscribeToUserNotifications, getUserNotifications, markNotificationAsRead } from '../lib/supabase'
import { useAuth } from './AuthContext'
import toast from 'react-hot-toast'
import { EnterpriseToast } from '../components/ui/EnterpriseToast'

interface NotificationContextType {
  notifications: SecretNotification[]
  unreadCount: number
  loading: boolean
  markAsRead: (notificationId: string) => Promise<void>
  clearAll: () => Promise<void>
  sendTestNotification: (type: 'sms' | 'email' | 'push', message: string) => Promise<void>
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined)

export const useNotifications = () => {
  const context = useContext(NotificationContext)
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider')
  }
  return context
}

interface NotificationProviderProps {
  children: ReactNode
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const { user } = useAuth()
  const [notifications, setNotifications] = useState<SecretNotification[]>([])
  const [loading, setLoading] = useState(true)

  // Calculate unread count
  const unreadCount = notifications.filter(n => !n.read_at).length

  // Load notifications when user changes
  useEffect(() => {
    let mounted = true

    const loadNotifications = async () => {
      if (!user) {
        setNotifications([])
        setLoading(false)
        return
      }

      try {
        const userNotifications = await getUserNotifications(user.id)
        if (mounted) {
          setNotifications(userNotifications)
        }
      } catch (error) {
        console.error('Error loading notifications:', error)
        if (mounted) {
          toast.custom((t) => (
            <EnterpriseToast
              id={t.id}
              type="error"
              title="Loading Failed"
              message="Could not load your notifications"
              visible={t.visible}
            />
          ))
        }
      } finally {
        if (mounted) {
          setLoading(false)
        }
      }
    }

    loadNotifications()

    return () => {
      mounted = false
    }
  }, [user])

  // Subscribe to real-time notifications
  useEffect(() => {
    if (!user) return

    const subscription = subscribeToUserNotifications(
      user.id,
      (newNotification) => {
        setNotifications(prev => [newNotification, ...prev])

        // Show toast for new notification
        toast.custom((t) => (
          <EnterpriseToast
            id={t.id}
            type="success"
            title={newNotification.title}
            message={newNotification.message}
            visible={t.visible}
          />
        ), {
          duration: 5000
        })
      }
    )

    return () => {
      subscription.unsubscribe()
    }
  }, [user])

  // Mark notification as read
  const markAsRead = async (notificationId: string) => {
    try {
      await markNotificationAsRead(notificationId)
      
      setNotifications(prev => 
        prev.map(notification => 
          notification.id === notificationId 
            ? { ...notification, read_at: new Date().toISOString() }
            : notification
        )
      )
    } catch (error) {
      console.error('Error marking notification as read:', error)
      toast.custom((t) => (
        <EnterpriseToast
          id={t.id}
          type="error"
          title="Update Failed"
          message="Could not mark notification as read"
          visible={t.visible}
        />
      ))
    }
  }

  // Clear all notifications
  const clearAll = async () => {
    try {
      const unreadNotifications = notifications.filter(n => !n.read_at)
      
      await Promise.all(
        unreadNotifications.map(notification => 
          markNotificationAsRead(notification.id)
        )
      )

      setNotifications(prev =>
        prev.map(notification => ({
          ...notification,
          read_at: notification.read_at || new Date().toISOString()
        }))
      )

      toast.custom((t) => (
        <EnterpriseToast
          id={t.id}
          type="success"
          title="All Cleared"
          message="All notifications have been marked as read"
          visible={t.visible}
        />
      ))
    } catch (error) {
      console.error('Error clearing notifications:', error)
      toast.custom((t) => (
        <EnterpriseToast
          id={t.id}
          type="error"
          title="Clear Failed"
          message="Could not clear notifications. Please try again"
          visible={t.visible}
        />
      ))
    }
  }

  // Send test notification (for development/testing)
  const sendTestNotification = async (type: 'sms' | 'email' | 'push', message: string) => {
    if (!user) return

    try {
      const { data, error } = await supabase
        .from('secret_notifications')
        .insert([
          {
            user_id: user.id,
            type: 'system',
            title: `Test ${type.toUpperCase()} Notification`,
            message,
            channels: [type],
            sent_at: new Date().toISOString()
          }
        ])
        .select()
        .single()

      if (error) throw error

      toast.custom((t) => (
        <EnterpriseToast
          id={t.id}
          type="success"
          title="Test Sent"
          message={`Test ${type} notification has been sent`}
          visible={t.visible}
        />
      ))
    } catch (error) {
      console.error('Error sending test notification:', error)
      toast.custom((t) => (
        <EnterpriseToast
          id={t.id}
          type="error"
          title="Send Failed"
          message="Could not send test notification"
          visible={t.visible}
        />
      ))
    }
  }

  const value: NotificationContextType = {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    clearAll,
    sendTestNotification
  }

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  )
}

export default NotificationProvider